// src/pages/qc/PageQCHmAnalysis.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { HmAnalysisModal } from "./HmAnalysisModal";
import { MonthYearSelect } from "../../components/MonthYearSelect";
import { currentMonthKey } from "../../utils/dateUtils";
import { useToast } from "../../components/Toast";

function fv(value, min, max) {
  if (value == null) return "SKIP";
  if (min == null && max == null) return "SKIP";
  const v = parseFloat(value);
  if ((min != null && v < parseFloat(min)) || (max != null && v > parseFloat(max))) return "FAIL";
  return "PASS";
}

function rowVerdict(row, spec) {
  if (!spec) return "NS";
  const isPSA = spec.peelAdhesionMin != null || spec.peelAdhesionMax != null;
  const verdicts = [
    fv(row.viscosity,   spec.viscosityMin,    spec.viscosityMax),
    fv(row.softeningPt, spec.softeningPtMin,  spec.softeningPtMax),
    ...(isPSA
      ? [fv(row.peelAdhesion, spec.peelAdhesionMin, spec.peelAdhesionMax),
         fv(row.loopTack,     spec.loopTackMin,      spec.loopTackMax)]
      : [fv(row.setTime,  spec.setTimeMin,  spec.setTimeMax),
         fv(row.openTime, spec.openTimeMin, spec.openTimeMax)]
    ),
  ].filter(v => v !== "SKIP");

  if (verdicts.length === 0) return "NS";
  if (verdicts.includes("FAIL")) return "FAIL";
  return "PASS";
}

function VerdictBadge({ verdict }) {
  const cfg = {
    PASS: { bg:"#d1fae5", color:"#065f46", border:"#6ee7b7", label:"PASS" },
    FAIL: { bg:"#fee2e2", color:"#991b1b", border:"#fca5a5", label:"FAIL" },
    NS:   { bg:"#fef9c3", color:"#854d0e", border:"#fde047", label:"N/S"  },
  };
  const c = cfg[verdict] ?? cfg.NS;
  return (
    <span style={{ background:c.bg, color:c.color, border:`1px solid ${c.border}`,
      borderRadius:4, padding:"2px 8px", fontSize:11, fontWeight:600 }}>
      {c.label}
    </span>
  );
}

const ROW_BG = { PASS:"#f0fdf4", FAIL:"#fff1f2", NS:"#fefce8" };
const FAIL_CELL = { background:"#fee2e2", color:"#991b1b", fontWeight:600 };

function FC({ verdict, children }) {
  return <td style={verdict === "FAIL" ? FAIL_CELL : {}}>{children}</td>;
}

function fmt(v) { return v != null ? String(v) : "—"; }

export function PageQCHmAnalysis({ myPerms }) {
  const showToast = useToast();
  const [rows,           setRows]           = useState([]);
  const [specs,          setSpecs]          = useState([]);
  const [grades,         setGrades]         = useState([]);
  const [displayNameMap, setDisplayNameMap] = useState({});
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [search,         setSearch]         = useState("");
  const [monthKey,       setMonthKey]       = useState(currentMonthKey);
  const [modal,          setModal]          = useState(false);

  const canDelete = myPerms?.includes("USER_MANAGE");

  useEffect(() => {
    Promise.all([api.fetchHmAnalyses(), api.fetchHmSpecs(), api.fetchHmGrades()])
      .then(([analyses, hmSpecs, hmGrades]) => {
        setRows(analyses);
        setSpecs(hmSpecs);
        setGrades(hmGrades);
        const m = {};
        hmGrades.forEach(g => { m[g.id] = g.displayName || g.id; });
        setDisplayNameMap(m);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const specMap = useMemo(() => {
    const m = {};
    specs.forEach(s => { m[s.hmId] = s; });
    return m;
  }, [specs]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r =>
      (r.date || "").startsWith(monthKey) &&
      (r.hmId.toLowerCase().includes(q) ||
       (displayNameMap[r.hmId] || "").toLowerCase().includes(q) ||
       (r.batchNo || "").toLowerCase().includes(q))
    );
  }, [rows, search, monthKey, displayNameMap]);

  async function handleCreate(body) {
    const created = await api.createHmAnalysis(body);
    setRows(old => [created, ...old]);
    showToast("Analysis saved");
    setModal(false);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this analysis record?")) return;
    await api.deleteHmAnalysis(id);
    setRows(old => old.filter(r => r.id !== id));
    showToast("Record deleted", "info");
  }

  const colCount = canDelete ? 10 : 9;

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Hotmelt Analysis</div>
          <div className="page-subtitle">Pass / Fail verdict against Hotmelt &amp; PSA specifications</div>
        </div>
        <div className="page-header-right">
          <button className="btn btn-primary" onClick={() => setModal(true)}>+ Add Analysis</button>
        </div>
      </div>

      <div className="action-bar">
        <MonthYearSelect value={monthKey} onChange={setMonthKey} />
        <input className="form-input" placeholder="Search grade or batch…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ width:240 }} />
      </div>

      {loading && <div style={{ color:"#aaa", fontSize:12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Grade</th>
                <th>Batch No</th>
                <th>Viscosity (cps)</th>
                <th>Softening Pt (°C)</th>
                <th>Set Time (s)</th>
                <th>Open Time (s)</th>
                <th>Peel / Loop Tack</th>
                <th>Verdict</th>
                {canDelete && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => {
                const spec    = specMap[row.hmId];
                const verdict = rowVerdict(row, spec);
                const bg      = ROW_BG[verdict] ?? "#fff";
                const isPSA   = spec && (spec.peelAdhesionMin != null || spec.peelAdhesionMax != null);

                const viscV  = fv(row.viscosity,   spec?.viscosityMin,   spec?.viscosityMax);
                const softV  = fv(row.softeningPt, spec?.softeningPtMin, spec?.softeningPtMax);
                const setV   = fv(row.setTime,     spec?.setTimeMin,     spec?.setTimeMax);
                const openV  = fv(row.openTime,    spec?.openTimeMin,    spec?.openTimeMax);
                const peelV  = fv(row.peelAdhesion,spec?.peelAdhesionMin,spec?.peelAdhesionMax);
                const tackV  = fv(row.loopTack,    spec?.loopTackMin,    spec?.loopTackMax);

                return (
                  <tr key={row.id} style={{ background: bg }}>
                    <td style={{ whiteSpace:"nowrap" }}>{row.date}</td>
                    <td className="fw-600">
                      {displayNameMap[row.hmId] || row.hmId}
                      {isPSA
                        ? <span className="badge badge-purple" style={{ marginLeft:6 }}>PSA</span>
                        : <span className="badge badge-green"  style={{ marginLeft:6 }}>HM</span>}
                    </td>
                    <td>{row.batchNo || "—"}</td>
                    <FC verdict={viscV}>
                      {row.viscosity != null
                        ? `${row.viscosity}${row.viscosityTempC != null ? ` @ ${row.viscosityTempC}°C` : ""}`
                        : "—"}
                    </FC>
                    <FC verdict={softV}>{fmt(row.softeningPt)}</FC>
                    <FC verdict={setV}>{!isPSA ? fmt(row.setTime) : "—"}</FC>
                    <FC verdict={openV}>{!isPSA ? fmt(row.openTime) : "—"}</FC>
                    <td>
                      {isPSA ? (
                        <span>
                          <FC verdict={peelV}>{fmt(row.peelAdhesion)}</FC>
                          {" / "}
                          <FC verdict={tackV}>{fmt(row.loopTack)}</FC>
                        </span>
                      ) : "—"}
                    </td>
                    <td><VerdictBadge verdict={verdict} /></td>
                    {canDelete && (
                      <td>
                        <button className="btn-small btn-danger" onClick={() => handleDelete(row.id)}>Del</button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={colCount} style={{ textAlign:"center", color:"#aaa", padding:24 }}>
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setModal(false); }}>
          <HmAnalysisModal
            specs={specs}
            grades={grades}
            onSave={handleCreate}
            onCancel={() => setModal(false)}
          />
        </div>
      )}
    </>
  );
}
